import { __decorate } from "tslib";
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import HelixElement from '../../shared/types/helix-element.js';
import { localized, msg } from '@lit/localize';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { carouselStyles } from './carousel.styles.js';
import '../icon/icon.js';
import '../button/button.js';
import Swiper from 'swiper';
import { A11y, FreeMode, Keyboard, Manipulation, Mousewheel, Navigation, Scrollbar, } from 'swiper/modules';
let HlxCarousel = class HlxCarousel extends HelixElement {
    constructor() {
        super(...arguments);
        this.inverted = false;
        this.dragging = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('role', 'region');
        this.setAttribute('aria-label', msg('carousel'));
    }
    firstUpdated() {
        this.swiper = new Swiper(this.carouselEl, {
            modules: [A11y, FreeMode, Keyboard, Manipulation, Mousewheel, Navigation, Scrollbar],
            breakpoints: {
                0: {
                    slidesPerView: 1.1,
                    spaceBetween: 16,
                },
                768: {
                    slidesPerView: 2.1,
                    spaceBetween: 24,
                },
                1200: {
                    slidesPerView: 2.5,
                    spaceBetween: 32,
                },
            },
            // centeredSlides: true,
            // centeredSlidesBounds: true,
            freeMode: {
                enabled: true,
                sticky: true,
            },
            grabCursor: true,
            keyboard: {
                enabled: true,
            },
            mousewheel: {
                enabled: true,
                forceToAxis: true,
            },
            navigation: {
                enabled: true,
                prevEl: this.prevButtonEl.shadowRoot.querySelector('button'),
                nextEl: this.nextButtonEl.shadowRoot.querySelector('button'),
            },
            scrollbar: {
                enabled: true,
                el: this.scrollBarEl,
                dragClass: 'hlx-carousel-scroll-bar-indicator',
                draggable: true,
                snapOnRelease: true,
            },
            simulateTouch: true,
            slideClass: 'swiper-slide',
            speed: 400,
            wrapperClass: 'swiper-wrapper',
            on: {
                reachBeginning: (swiper) => {
                    this.prevEnabled = !swiper.isBeginning;
                },
                reachEnd: (swiper) => {
                    this.nextEnabled = !swiper.isEnd;
                },
                fromEdge: (swiper) => {
                    this.prevEnabled = !swiper.isBeginning;
                    this.nextEnabled = !swiper.isEnd;
                },
                scrollbarDragStart: (swiper, event) => {
                    this.dragging = true;
                },
                scrollbarDragEnd: (swiper, event) => {
                    this.dragging = false;
                },
            },
        });
    }
    render() {
        return html `
            <div
                class="${classMap({
            'hlx-carousel': true,
            'hlx-carousel-inverted': this.inverted,
            'hlx-carousel-dragging': this.dragging,
            swiper: true,
        })}">
                <slot
                    id="scroll-container"
                    part="scroll-container"
                    @slotchange=${this.initializeSlides}
                    class="${classMap({
            'hlx-carousel-slides': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'swiper-wrapper': true,
        })}"
                    aria-atomic="true"></slot>

                <div class="hlx-carousel-actions">
                    <div
                        part="scroll-bar"
                        class="hlx-carousel-scroll-bar swiper-scrollbar"
                        aria-controls="scroll-container"
                        tabindex="0">
                        <div class="hlx-carousel-scroll-bar-track">
                            <div
                                class="hlx-carousel-scroll-bar-indicator hlx-animation-with-prefers-reduced-motion"></div>
                        </div>
                    </div>

                    <div class="hlx-carousel-button-group">
                        <hlx-button
                            class="hlx-carousel-previous-button"
                            kind="${this.prevEnabled ? 'secondary' : 'tertiary'}"
                            type="button"
                            variant="${this.inverted ? 'inverted' : 'dark'}"
                            size="small"
                            ?disabled=${!this.prevEnabled}
                            @click="${this.prevEnabled ? () => this.prev() : null}"
                            aria-controls="scroll-container">
                            <hlx-icon
                                name="arrow-left"
                                size="14"
                                slot="icon"
                                title="${msg('Previous button')}"></hlx-icon>
                        </hlx-button>

                        <hlx-button
                            class="hlx-carousel-next-button"
                            kind="${this.nextEnabled ? 'secondary' : 'tertiary'}"
                            type="button"
                            variant="${this.inverted ? 'inverted' : 'dark'}"
                            size="small"
                            ?disabled=${!this.nextEnabled}
                            @click="${this.nextEnabled ? () => this.next() : null}"
                            aria-controls="scroll-container">
                            <hlx-icon
                                name="arrow-right"
                                size="14"
                                slot="icon"
                                title="${msg('Next button')}"></hlx-icon>
                        </hlx-button>
                    </div>
                </div>

                <span class="swiper-notification" aria-live="assertive" aria-atomic="true"></span>
            </div>
        `;
    }
    prev() {
        this.swiper.slidePrev();
    }
    next() {
        this.swiper.slideNext();
    }
    initializeSlides() {
        const slides = this.getSlides();
        slides.forEach((s) => {
            s.classList.add('swiper-slide');
        });
        this.swiper.updateSlides();
        this.prevEnabled = !this.swiper.isBeginning;
        this.nextEnabled = !this.swiper.isEnd;
    }
    getSlides() {
        return [...this.children].filter((el) => this.isCarouselItem(el));
    }
    isCarouselItem(node) {
        return node instanceof Element && node.tagName.toLowerCase() === 'hlx-carousel-item';
    }
};
HlxCarousel.styles = [componentStyles, carouselStyles];
__decorate([
    property({ type: Boolean, reflect: true })
], HlxCarousel.prototype, "inverted", void 0);
__decorate([
    state()
], HlxCarousel.prototype, "dragging", void 0);
__decorate([
    query('.hlx-carousel')
], HlxCarousel.prototype, "carouselEl", void 0);
__decorate([
    query('.hlx-carousel-scroll-bar')
], HlxCarousel.prototype, "scrollBarEl", void 0);
__decorate([
    query('.hlx-carousel-previous-button')
], HlxCarousel.prototype, "prevButtonEl", void 0);
__decorate([
    query('.hlx-carousel-next-button')
], HlxCarousel.prototype, "nextButtonEl", void 0);
__decorate([
    state()
], HlxCarousel.prototype, "prevEnabled", void 0);
__decorate([
    state()
], HlxCarousel.prototype, "nextEnabled", void 0);
HlxCarousel = __decorate([
    customElement('hlx-carousel'),
    localized()
], HlxCarousel);
export { HlxCarousel };
//# sourceMappingURL=carousel.js.map